import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  OrderStatus,
  PaymentType,
  PaymentMode,
  Supplier,
} from "../../../core/common/selectOption/selectOption";
import { Link } from "react-router-dom";
import {
  Calendar,
  DollarSign,
  Minus,
  PlusCircle,
  ArrowLeft,
} from "feather-icons-react/build/IconComponents";
import { DatePicker } from "antd";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import DefaultEditor from "react-simple-wysiwyg";
import CustomerServices from "../../../services/CustomerServices";
import EmployeeServices from "../../../services/EmployeeServices";
import ServiceServices from "../../../services/ServiceServices";
import useStore from "../../../store/store";
import useAsync from "../../../hooks/useAsync";
import { useBilling } from "../../../hooks/useBilling";

const OnlineorderModal = () => {
  const [editorValue, setEditorValue] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("+91");
  const [isCustomerExists, setIsCustomerExists] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [services, setServices] = useState([
    {
      id: 1,
      service: null,
      quantity: 1,
      price: "",
      assignedEmployee: null,
    },
  ]);

  const selectedStore = useStore((state) => state.selectedStore);
  const storeId = selectedStore?._id;

  // Fetch customers
  const { data: customers } = useAsync(() => {
    const fetchStoreCustomers = async () => {
      if (!storeId) {
        return [];
      }
      const response = await CustomerServices.getAllCustomers(storeId);
      return response.map((customer) => ({
        value: customer._id,
        label: customer.name,
        phone: customer.phone,
      }));
    };
    return fetchStoreCustomers();
  }, [storeId]);

  // Fetch employees
  const { data: employees } = useAsync(() => {
    const fetchEmployees = async () => {
      if (!storeId) {
        return [];
      }
      const response = await EmployeeServices.getEmployees(storeId);
      return response.map((employee) => ({
        value: employee._id,
        label: employee.name,
      }));
    };
    return fetchEmployees();
  }, [storeId]);

  // Fetch services
  const { data: serviceOptions } = useAsync(() => {
    const fetchServices = async () => {
      if (!storeId) return [];
      const response = await ServiceServices.getServices(storeId);
      return response.map((service) => ({
        value: service._id,
        label: service.name,
        price: service.price,
      }));
    };
    return fetchServices();
  }, [storeId]);

  // State for calculations
  const [totals, setTotals] = useState({
    grossTotal: 0,
    discount: 0,
    cgst: 0,
    sgst: 0,
    netPayable: 0,
  });

  // Calculate totals
  const calculateTotals = (updatedServices) => {
    const grossTotal = updatedServices.reduce(
      (sum, service) => sum + service.price * service.quantity,
      0
    );

    const discount = parseFloat(localStorage.getItem("discount") || 0);
    const afterDiscount = grossTotal - discount;
    const cgst = afterDiscount * 0.025; // 2.5% CGST
    const sgst = afterDiscount * 0.025; // 2.5% SGST
    const netPayable = afterDiscount + cgst + sgst;

    const newTotals = {
      grossTotal,
      discount,
      afterDiscount,
      cgst,
      sgst,
      netPayable,
    };

    setTotals(newTotals);
    localStorage.setItem("totals", JSON.stringify(newTotals));
  };

  // Filter out already selected services from options
  const getAvailableServices = (currentIndex) => {
    const selectedServiceIds = services.map(
      (s, idx) => idx !== currentIndex && s.service?.value
    );
    return serviceOptions?.filter(
      (option) => !selectedServiceIds.includes(option.value)
    );
  };

  // Handle service change
  const handleServiceChange = (value, index) => {
    const updatedServices = [...services];
    const selectedService = serviceOptions.find(
      (option) => option.value === value.value
    );

    updatedServices[index] = {
      ...updatedServices[index],
      service: value,
      price: selectedService.price,
      totalPrice: selectedService.price * updatedServices[index].quantity,
    };

    setServices(updatedServices);
    localStorage.setItem("salonServices", JSON.stringify(updatedServices));
    calculateTotals(updatedServices);
  };

  // Handle price change
  const handlePriceChange = (value, index) => {
    const updatedServices = [...services];
    updatedServices[index].price = value;
    setServices(updatedServices);
  };

  // Handle quantity change
  const handleQuantityChange = (action, index) => {
    const updatedServices = [...services];
    let newQuantity = updatedServices[index].quantity;

    if (action === "increment") {
      newQuantity += 1;
    } else if (action === "decrement" && newQuantity > 1) {
      newQuantity -= 1;
    }

    updatedServices[index] = {
      ...updatedServices[index],
      quantity: newQuantity,
      totalPrice: updatedServices[index].price * newQuantity,
    };

    setServices(updatedServices);
    localStorage.setItem("salonServices", JSON.stringify(updatedServices));
    calculateTotals(updatedServices);
  };

  // Handle employee assignment
  const handleEmployeeChange = (value, index) => {
    const updatedServices = [...services];
    updatedServices[index].assignedEmployee = value;
    setServices(updatedServices);
  };

  // Add new service row
  const handleAddService = () => {
    setServices([
      ...services,
      {
        id: services.length + 1,
        service: null,
        price: "",
        quantity: 1,
        assignedEmployee: null,
      },
    ]);
  };

  // Remove service row
  const handleRemoveService = (index) => {
    const updatedServices = services.filter((_, i) => i !== index);
    setServices(updatedServices);
  };

  // Handle discount change
  const handleDiscountChange = (value) => {
    const discount = parseFloat(value) || 0;
    localStorage.setItem("discount", discount);
    calculateTotals(services);
  };

  // Update the Select component for customer selection
  const handleCustomerChange = (e) => {
    setSelectedCustomer(e.target.value);
    setCustomerName(e.target.value);
  };

  const handlePhoneChange = (e) => {
    let input = e.target.value;

    // If user deletes the +91 prefix, add it back
    if (!input.startsWith("+91")) {
      input = input.replace(/\D/g, ""); // Remove non-digits
      input = "+91" + input;
    } else {
      // Keep the +91 prefix and clean the rest
      input = "+91" + input.substring(3).replace(/\D/g, "");
    }

    // Limit to +91 plus 10 digits
    if (input.length <= 13) {
      setPhoneNumber(input);
    }
  };

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const customer = await CustomerServices.getCustomerByPhone(
          storeId,
          phoneNumber
        );
        if (customer && customer._id) {
          setSelectedCustomer(customer._id);
          setIsCustomerExists(true);
          // Bind customer name to the textbox if customer exists
          setCustomerName(customer.name);
        } else {
          setSelectedCustomer(null);
          setIsCustomerExists(false);
          // Clear customer name if customer doesn't exist
          setCustomerName("");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setIsCustomerExists(false);
        setCustomerName("");
      }
    };

    if (phoneNumber.length === 13) {
      // +91 + 10 digits
      fetchCustomer();
    } else {
      setSelectedCustomer(null); // Clear selection if phone becomes invalid
      setIsCustomerExists(false);
      setCustomerName("");
    }
  }, [phoneNumber, storeId]);

  // Handle increment/decrement
  const handleIncrement = (index) => {
    handleQuantityChange("increment", index);
  };

  const handleDecrement = (index) => {
    handleQuantityChange("decrement", index);
  };

  // Handle editor changes
  const handleEditorChange = (value) => {
    setEditorValue(value);
  };

  const handlePaymentMethodChange = (selected) => {
    setPaymentMethod(selected);
  };

  // Use the billing hook
  const { createBilling } = useBilling();

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate services
    for (let i = 0; i < services.length; i++) {
      const service = services[i];
      if (!service.service?.value) {
        alert(`Please select a service for row ${i + 1}`);
        return;
      }
      if (!service.assignedEmployee?.value) {
        alert(`Please assign an employee for service in row ${i + 1}`);
        return;
      }
      if (!service.price || service.price <= 0) {
        alert(`Please enter a valid price for service in row ${i + 1}`);
        return;
      }
    }

    // Validate payment method
    if (!paymentMethod?.value) {
      alert("Please select a payment method");
      return;
    }

    try {
      const billingData = {
        customer: selectedCustomer,
        date: new Date(),
        services: services.map((service) => ({
          service: service.service?.value,
          quantity: service.quantity,
          price: service.price,
          assignedEmployee: service.assignedEmployee?.value,
          totalPrice: service.quantity * service.price,
        })),
        totals: {
          grossTotal: totals.grossTotal,
          discount: totals.discount,
          cgst: totals.cgst,
          sgst: totals.sgst,
          netPayable: totals.netPayable,
        },
        paymentMethod: paymentMethod?.value,
        status: "pending",
        storeId: storeId,
        isCustomerExists: isCustomerExists,
        customerPhone: phoneNumber, // Include phone number for creating new customer if needed
        customerName: customerName, // Include customer name for creating new customer if needed
      };

      // Use createBilling from useBilling hook instead of direct service call
      // This will automatically update the billings state in the useBilling hook
      await createBilling(billingData);

      // Reset form
      setServices([
        {
          id: 1,
          service: null,
          quantity: 1,
          price: "",
          assignedEmployee: null,
        },
      ]);
      setTotals({
        grossTotal: 0,
        discount: 0,
        cgst: 0,
        sgst: 0,
        netPayable: 0,
      });
      setPaymentMethod(null);
      setEditorValue("");

      // Close modal
      // const modal = document.getElementById("add-sales-new");
      // if (modal) {
      //   modal.classList.remove("show");
      //   modal.style.display = "none";
      //   document.body.classList.remove("modal-open");
      //   const backdrop = document.querySelector(".modal-backdrop");
      //   if (backdrop) {
      //     backdrop.remove();
      //   }
      // }
    } catch (err) {
      console.error("Error creating billing:", err);
    } finally {
      window.location.reload();
    }
  };

  return (
    <div>
      <>
        {/*add popup */}
        <div className="modal fade" id="add-sales-new">
          <div className="modal-dialog add-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div className="page-title">
                  <h4> Add Sales</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="card border-0">
                  <div className="card-body pb-0">
                    <div className="table-responsive no-pagination mb-3">
                      <table className="table datanew">
                        <thead>
                          <tr>
                            <th>Service</th>
                            <th>Qty</th>
                            <th>Purchase Price(₹)</th>
                            <th>Discount(₹)</th>
                            <th>Tax(%)</th>
                            <th>Tax Amount(₹)</th>
                            <th>Unit Cost(₹)</th>
                            <th>Total Cost(%)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td />
                            <td />
                            <td />
                            <td />
                            <td />
                            <td />
                            <td />
                            <td />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="row">
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Phone<span className="text-danger ms-1">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={phoneNumber}
                            onChange={handlePhoneChange}
                          />
                        </div>
                      </div>
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Customer Name
                            <span className="text-danger ms-1">*</span>
                          </label>
                          <div className="row">
                            <div className="col-lg-10 col-sm-10 col-10">
                              <input
                                className="form-control"
                                name="customerName"
                                value={customerName}
                                onChange={handleCustomerChange}
                              />
                              {phoneNumber.length === 13 && (
                                <small
                                  className={
                                    isCustomerExists
                                      ? "text-success"
                                      : "text-danger"
                                  }
                                >
                                  {isCustomerExists
                                    ? "Customer exists"
                                    : "New customer"}
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Date<span className="text-danger ms-1">*</span>
                          </label>
                          <div className="input-groupicon calender-input">
                            <DatePicker
                              className="form-control datetimepicker"
                              placeholder="dd/mm/yyyy"
                            />
                            <Calendar className="info-img" />
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        {services.map((service, index) => (
                          <div
                            key={service.id}
                            className="row align-items-center mb-3"
                          >
                            <div className="col-lg-3 col-sm-6 col-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Service
                                  <span className="text-danger ms-1">*</span>
                                </label>
                                <Select
                                  classNamePrefix="react-select"
                                  options={getAvailableServices(index)}
                                  value={service.service}
                                  onChange={(value) =>
                                    handleServiceChange(value, index)
                                  }
                                  placeholder="Choose Service"
                                />
                              </div>
                            </div>
                            <div className="col-lg-2 col-sm-6 col-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Qty<span className="text-danger ms-1">*</span>
                                </label>
                                <div className="product-quantity bg-gray-transparent border-0">
                                  <span
                                    className="quantity-btn"
                                    onClick={() =>
                                      handleQuantityChange("decrement", index)
                                    }
                                  >
                                    <Minus />
                                  </span>
                                  <input
                                    type="text"
                                    className="quntity-input form-control bg-transparent"
                                    value={service.quantity}
                                    readOnly
                                  />
                                  <span
                                    className="quantity-btn"
                                    onClick={() =>
                                      handleQuantityChange("increment", index)
                                    }
                                  >
                                    <PlusCircle />
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-3 col-sm-6 col-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Price
                                  <span className="text-danger ms-1">*</span>
                                </label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={service.price}
                                  onChange={(e) =>
                                    handlePriceChange(e.target.value, index)
                                  }
                                  placeholder="Enter price"
                                />
                              </div>
                            </div>
                            <div className="col-lg-3 col-sm-6 col-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Assigned Employee
                                  <span className="text-danger ms-1">*</span>
                                </label>
                                <Select
                                  classNamePrefix="react-select"
                                  options={employees}
                                  value={service.assignedEmployee}
                                  onChange={(value) =>
                                    handleEmployeeChange(value, index)
                                  }
                                  placeholder="Choose Employee"
                                />
                              </div>
                            </div>
                            <div className="col-lg-1 col-sm-6 col-6 d-flex align-items-end">
                              {services.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={() => handleRemoveService(index)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        <div className="row">
                          <div className="col-12">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleAddService}
                            >
                              <PlusCircle className="me-1" /> Add Service
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-6 ms-auto">
                        <div className="total-order w-100 max-widthauto m-auto mb-4">
                          <ul className="border-1 rounded-2">
                            <li className="border-bottom">
                              <h4 className="border-end">Gross Total</h4>
                              <h5>₹ {totals.grossTotal.toFixed(2)}</h5>
                            </li>
                            <li className="border-bottom">
                              <h4 className="border-end">Discount</h4>
                              <h5>₹ {totals.discount.toFixed(2)}</h5>
                            </li>
                            <li className="border-bottom">
                              <h4 className="border-end">CGST (2.5%)</h4>
                              <h5>₹ {totals.cgst.toFixed(2)}</h5>
                            </li>
                            <li className="border-bottom">
                              <h4 className="border-end">SGST (2.5%)</h4>
                              <h5>₹ {totals.sgst.toFixed(2)}</h5>
                            </li>
                            <li className="border-bottom">
                              <h4 className="border-end">Net Payable</h4>
                              <h5>₹ {totals.netPayable.toFixed(2)}</h5>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Discount<span className="text-danger ms-1">*</span>
                          </label>
                          <div className="input-groupicon select-code">
                            <input
                              type="text"
                              defaultValue={0}
                              className="form-control p-2"
                              onChange={(e) =>
                                handleDiscountChange(e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3 mb-5">
                          <label className="form-label">
                            Payment Method
                            <span className="text-danger ms-1">*</span>
                          </label>
                          <Select
                            classNamePrefix="react-select"
                            options={PaymentMode}
                            placeholder="Choose"
                            name="paymentMethod"
                            onChange={handlePaymentMethodChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary add-cancel me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      phoneNumber.length === 13 &&
                      !selectedCustomer &&
                      !isCustomerExists
                    }
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /add popup */}
        {/* details popup */}
        <div className="modal fade" id="sales-details-new">
          <div className="modal-dialog sales-details-modal">
            <div className="modal-content">
              <div className="page-header p-4 border-bottom mb-0">
                <div className="add-item d-flex align-items-center">
                  <div className="page-title modal-datail">
                    <h4 className="mb-0 me-2">Sales Detail</h4>
                  </div>
                </div>
                <ul className="table-top-head">
                  <li>
                    <Link
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Pdf"
                    >
                      <ImageWithBasePath
                        src="assets/img/icons/pdf.svg"
                        alt="img"
                      />
                    </Link>
                  </li>
                  <li>
                    <Link
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Pdf"
                    >
                      <ImageWithBasePath
                        src="assets/img/icons/printer.svg"
                        alt="img"
                      />
                    </Link>
                  </li>
                </ul>
                <div className="page-btn">
                  <Link
                    to="#"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    <ArrowLeft className="me-2" /> Back to Sales
                  </Link>
                </div>
              </div>
              <form>
                <div className="card border-0">
                  <div className="card-body pb-0">
                    <div
                      className="invoice-box table-height"
                      style={{
                        maxWidth: 1600,
                        width: "100%",
                        padding: 0,
                        fontSize: 14,
                        color: "#555",
                      }}
                    >
                      <div className="row sales-details-items d-flex">
                        <div className="col-md-4 details-item">
                          <h6>Customer Info</h6>
                          <h4 className="mb-1">Carl Evans</h4>
                          <p className="mb-0">
                            3103 Trainer Avenue Peoria, IL 61602
                          </p>
                          <p className="mb-0">
                            Email<span>carlevans241@example.com</span>
                          </p>
                          <p className="mb-0">
                            Phone<span>+1 987 471 6589</span>
                          </p>
                        </div>
                        <div className="col-md-4 details-item">
                          <h6>Company Info</h6>
                          <h4 className="mb-1">DGT</h4>
                          <p className="mb-0">
                            2077 Chicago Avenue Orosi, CA 93647
                          </p>
                          <p className="mb-0">
                            Email<span>admin@example.com</span>
                          </p>
                          <p className="mb-0">
                            Phone<span>+1 893 174 0385</span>
                          </p>
                        </div>
                        <div className="col-md-4 details-item">
                          <h6>Invoice Info</h6>
                          <p className="mb-0">
                            Reference:{" "}
                            <span className="fs-16 text-primary ms-2">
                              #SL0101
                            </span>
                          </p>
                          <p className="mb-0">
                            Reference:{" "}
                            <span className="ms-2 text-gray-9">
                              Dec 24, 2024
                            </span>
                          </p>
                          <p className="mb-0">
                            Status:{" "}
                            <span className="badge badge-success ms-2">
                              Completed
                            </span>
                          </p>
                          <p className="mb-0">
                            Payment Status:{" "}
                            <span className="badge badge-soft-success shadow-none badge-xs d-inline-flex align-items-center ms-2">
                              {" "}
                              <i className="ti ti-point-filled" />
                              Paid
                            </span>
                          </p>
                        </div>
                      </div>
                      <h5 className="order-text">Order Summary</h5>
                      <div className="table-responsive no-pagination mb-3">
                        <table className="table  datanew">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Purchase Price(₹)</th>
                              <th>Discount(₹)</th>
                              <th>Tax(%)</th>
                              <th>Tax Amount(₹)</th>
                              <th>Unit Cost(₹)</th>
                              <th>Total Cost(%)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Link
                                    to="#"
                                    className="avatar avatar-md me-2"
                                  >
                                    <ImageWithBasePath
                                      src="assets/img/products/stock-img-02.png"
                                      alt="product"
                                    />
                                  </Link>
                                  <Link to="#">Nike Jordan</Link>
                                </div>
                              </td>
                              <td>2000</td>
                              <td>500</td>
                              <td>0.00</td>
                              <td>0.00</td>
                              <td>0.00</td>
                              <td>1500</td>
                            </tr>
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Link
                                    to="#"
                                    className="avatar avatar-md me-2"
                                  >
                                    <ImageWithBasePath
                                      src="assets/img/products/stock-img-03.png"
                                      alt="product"
                                    />
                                  </Link>
                                  <Link to="#">Apple Series 5 Watch</Link>
                                </div>
                              </td>
                              <td>3000</td>
                              <td>400</td>
                              <td>0.00</td>
                              <td>0.00</td>
                              <td>0.00</td>
                              <td>1700</td>
                            </tr>
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Link
                                    to="#"
                                    className="avatar avatar-md me-2"
                                  >
                                    <ImageWithBasePath
                                      src="assets/img/products/stock-img-05.png"
                                      alt="product"
                                    />
                                  </Link>
                                  <Link to="#">Lobar Handy</Link>
                                </div>
                              </td>
                              <td>2500</td>
                              <td>500</td>
                              <td>0.00</td>
                              <td>0.00</td>
                              <td>0.00</td>
                              <td>2000</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="row">
                      <div className="row">
                        <div className="col-lg-6 ms-auto">
                          <div className="total-order w-100 max-widthauto m-auto mb-4">
                            <ul className="border-1 rounded-1">
                              <li className="border-bottom">
                                <h4 className="border-end">Order Tax</h4>
                                <h5>₹ 0.00</h5>
                              </li>
                              <li className="border-bottom">
                                <h4 className="border-end">Discount</h4>
                                <h5>₹ 0.00</h5>
                              </li>
                              <li className="border-bottom">
                                <h4 className="border-end">Grand Total</h4>
                                <h5>₹ 5200.00</h5>
                              </li>
                              <li className="border-bottom">
                                <h4 className="border-end">Paid</h4>
                                <h5>₹ 5200.00</h5>
                              </li>
                              <li className="border-bottom">
                                <h4 className="border-end">Due</h4>
                                <h5>₹ 0.00</h5>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /details popup */}
        {/* edit popup */}
        <div className="modal fade" id="edit-sales-new">
          <div className="modal-dialog edit-sales-modal">
            <div className="modal-content">
              <div className="page-wrapper p-0 m-0">
                <div className="content p-0">
                  <div className="page-header p-4 mb-0">
                    <div className="add-item new-sale-items d-flex">
                      <div className="page-title">
                        <h4>Edit Sales</h4>
                      </div>
                      <button
                        type="button"
                        className="close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    </div>
                  </div>
                  <form>
                    <div className="card border-0">
                      <div className="card-body pb-0">
                        <div className="table-responsive no-pagination mb-3">
                          <table className="table  datanew">
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th>Qty</th>
                                <th>Purchase Price(₹)</th>
                                <th>Discount(₹)</th>
                                <th>Tax(%)</th>
                                <th>Tax Amount(₹)</th>
                                <th>Unit Cost(₹)</th>
                                <th>Total Cost(%)</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Link
                                      to="#"
                                      className="avatar avatar-md me-2"
                                    >
                                      <ImageWithBasePath
                                        src="assets/img/products/stock-img-02.png"
                                        alt="product"
                                      />
                                    </Link>
                                    <Link to="#">Nike Jordan</Link>
                                  </div>
                                </td>
                                <td>
                                  <div className="product-quantity bg-gray-transparent border-0">
                                    <span
                                      className="quantity-btn"
                                      onClick={() => handleIncrement(0)}
                                    >
                                      <PlusCircle className="plus-circle" />
                                    </span>
                                    <input
                                      type="text"
                                      className="quntity-input form-control bg-transparent"
                                      defaultValue={2}
                                    />
                                    <span
                                      className="quantity-btn"
                                      onClick={() => handleDecrement(0)}
                                    >
                                      <Minus className="feather-search" />
                                    </span>
                                  </div>
                                </td>
                                <td>2000</td>
                                <td>500</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>1500</td>
                              </tr>
                              <tr>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Link
                                      to="#"
                                      className="avatar avatar-md me-2"
                                    >
                                      <ImageWithBasePath
                                        src="assets/img/products/stock-img-03.png"
                                        alt="product"
                                      />
                                    </Link>
                                    <Link to="#">Apple Series 5 Watch</Link>
                                  </div>
                                </td>
                                <td>
                                  <div className="product-quantity bg-gray-transparent border-0">
                                    <span
                                      className="quantity-btn"
                                      onClick={() => handleIncrement(1)}
                                    >
                                      <PlusCircle className="plus-circle" />
                                    </span>
                                    <input
                                      type="text"
                                      className="quntity-input form-control bg-transparent"
                                      defaultValue={2}
                                    />
                                    <span
                                      className="quantity-btn"
                                      onClick={() => handleDecrement(1)}
                                    >
                                      <Minus className="feather-search" />
                                    </span>
                                  </div>
                                </td>
                                <td>3000</td>
                                <td>400</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>1700</td>
                              </tr>
                              <tr>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Link
                                      to="#"
                                      className="avatar avatar-md me-2"
                                    >
                                      <ImageWithBasePath
                                        src="assets/img/products/stock-img-05.png"
                                        alt="product"
                                      />
                                    </Link>
                                    <Link to="#">Lobar Handy</Link>
                                  </div>
                                </td>
                                <td>
                                  <div className="product-quantity bg-gray-transparent border-0">
                                    <span
                                      className="quantity-btn"
                                      onClick={() => handleIncrement(2)}
                                    >
                                      <PlusCircle className="plus-circle" />
                                    </span>
                                    <input
                                      type="text"
                                      className="quntity-input form-control bg-transparent"
                                      defaultValue={2}
                                    />
                                    <span
                                      className="quantity-btn"
                                      onClick={() => handleDecrement(2)}
                                    >
                                      <Minus className="feather-search" />
                                    </span>
                                  </div>
                                </td>
                                <td>2500</td>
                                <td>500</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>2000</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="row">
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Customer Name
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="row">
                                <div className="col-lg-10 col-sm-10 col-10">
                                  <Select
                                    classNamePrefix="react-select"
                                    options={customers}
                                    placeholder="Choose"
                                  />
                                </div>
                                <div className="col-lg-2 col-sm-2 col-2 ps-0">
                                  <div className="add-icon">
                                    <Link
                                      to="#"
                                      className="bg-dark text-white p-2 rounded"
                                    >
                                      <PlusCircle className="plus" />
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Date<span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon calender-input">
                                <DatePicker
                                  className="form-control datetimepicker"
                                  placeholder="dd/mm/yyyy"
                                />
                                <Calendar className="info-img" />
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Supplier
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <Select
                                classNamePrefix="react-select"
                                options={Supplier}
                                placeholder="Choose"
                              />
                            </div>
                          </div>
                          <div className="col-lg-12 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Product Name
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon select-code">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Please type product code and select"
                                />
                                <div className="addonset">
                                  <ImageWithBasePath
                                    src="assets/img/icons/scanners.svg"
                                    alt="img"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6 ms-auto">
                            <div className="total-order w-100 max-widthauto m-auto mb-4">
                              <ul className="border-1 rounded-1">
                                <li className="border-bottom">
                                  <h4 className="border-end">Order Tax</h4>
                                  <h5>₹ 0.00</h5>
                                </li>
                                <li className="border-bottom">
                                  <h4 className="border-end">Discount</h4>
                                  <h5>₹ 0.00</h5>
                                </li>
                                <li className="border-bottom">
                                  <h4 className="border-end">Shipping</h4>
                                  <h5>₹ 0.00</h5>
                                </li>
                                <li className="border-bottom">
                                  <h4 className="border-end">Grand Total</h4>
                                  <h5>₹5200.00</h5>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Order Tax
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon select-code">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={0}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Discount
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon select-code">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={0}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Shipping
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon select-code">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={0}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="mb-3 mb-5">
                              <label className="form-label">
                                Status
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <Select
                                classNamePrefix="react-select"
                                options={OrderStatus}
                                placeholder="Choose"
                              />
                            </div>
                          </div>
                          <div className="col-lg-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Notes<span className="text-danger ms-1">*</span>
                              </label>
                              <DefaultEditor
                                value={editorValue}
                                onChange={handleEditorChange}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary add-cancel me-3"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <Link
                        to="#"
                        className="btn btn-primary add-sale"
                        data-bs-dismiss="modal"
                      >
                        Save Changes
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /edit popup */}
        {/* show payment Modal */}
        <div
          className="modal fade"
          id="showpayment"
          tabIndex={-1}
          aria-labelledby="showpayment"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered stock-adjust-modal">
            <div className="modal-content">
              <div className="modal-header">
                <div className="page-title">
                  <h4>Show Payments</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="modal-body-table total-orders p-0">
                      <div className="table-responsive rounded">
                        <table className="table datatable">
                          <thead className="thead-light">
                            <tr>
                              <th>Date</th>
                              <th>Reference</th>
                              <th>Amount</th>
                              <th>Paid By</th>
                              <th className="no-sort" />
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>19 Jan 2023</td>
                              <td>INV/SL0101</td>
                              <td>₹1500</td>
                              <td>Cash</td>
                              <td>
                                <div className="edit-delete-action d-flex align-items-center">
                                  <Link
                                    className="me-3 p-2 border rounded d-flex align-items-center"
                                    to="#"
                                  >
                                    <i
                                      data-feather="printer"
                                      className="feather-rotate-ccw"
                                    />
                                  </Link>
                                  <Link
                                    className="me-3 p-2 border rounded d-flex align-items-center"
                                    to="#"
                                    data-bs-toggle="modal"
                                    data-bs-target="#editpayment"
                                  >
                                    <i
                                      data-feather="edit"
                                      className="feather-edit"
                                    />
                                  </Link>
                                  <Link
                                    className="p-2 border rounded d-flex align-items-center"
                                    to="#"
                                  >
                                    <i
                                      data-feather="trash-2"
                                      className="feather-trash-2"
                                    />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* show payment Modal */}
        {/* Create payment Modal */}
        <div
          className="modal fade"
          id="createpayment"
          tabIndex={-1}
          aria-labelledby="createpayment"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div className="page-title">
                  <h4>Create Payments</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <form action="online-orders.html">
                <div className="modal-body">
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {" "}
                          Date<span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon calender-input">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder="dd/mm/yyyy"
                          />
                          <Calendar className="info-img" />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Reference<span className="text-danger ms-1">*</span>
                        </label>
                        <input type="text" className="form-control" />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Received Amount
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon doller-input">
                          <DollarSign className="info-img" />
                          <input type="text" className="form-control ps-4" />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Paying Amount
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon doller-input">
                          <DollarSign className="info-img" />
                          <input type="text" className="form-control ps-4" />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Payment type
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <Select
                          classNamePrefix="react-select"
                          options={PaymentType}
                          placeholder="Choose"
                        />
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label">Description</label>
                        <DefaultEditor
                          value={editorValue}
                          onChange={handleEditorChange}
                        />
                        <p>Maximum 60 Characters</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <Link
                    to="#"
                    className="btn btn-primary"
                    data-bs-dismiss="modal"
                  >
                    Submit
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* Create payment Modal */}
        {/* edit payment Modal */}
        <div
          className="modal fade"
          id="editpayment"
          tabIndex={-1}
          aria-labelledby="editpayment"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div className="page-title">
                  <h4>Edit Payments</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <form action="online-orders.html">
                <div className="modal-body">
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Date<span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon calender-input">
                          <i data-feather="calendar" className="info-img" />
                          <input
                            type="text"
                            className="datetimepicker form-control"
                            placeholder="Select Date"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Reference<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          defaultValue="INV/SL0101"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Received Amount
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon calender-input">
                          <i data-feather="dollar-sign" className="info-img" />
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={1500}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Paying Amount
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon calender-input">
                          <i data-feather="dollar-sign" className="info-img" />
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={1500}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Payment type
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <select className="select">
                          <option>Cash</option>
                          <option>Online</option>
                          <option>Inprogress</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3 summer-description-box transfer">
                        <label className="form-label">Description</label>
                        <div id="summernote2" />
                      </div>
                      <p>Maximum 60 Characters</p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* edit payment Modal */}
      </>
    </div>
  );
};

export default OnlineorderModal;
